import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
	AlertCircle,
	Award,
	Check,
	ChevronDown,
	ChevronUp,
	Link2,
	MessageSquare,
	MoreVertical,
	Pencil,
	Plus,
	SlidersHorizontal,
	Trash2,
	UserSquare2
} from 'lucide-react';
import type { AdditionalFiltering, ContributorToken } from '@sleed/bizcommerce-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { serverMessage } from '~/lib/api/server-error';
import { MultiSelect } from '~/components/multi-select';
import type { SortDir } from '~/lib/grid/sort';
import { useContributorsList, useContributorMutations, type ContributorSortKey } from '~/hooks/use-contributors';
import { useContributorCategoriesList } from '~/hooks/use-contributor-categories';
import { ContributorEditorSheet } from './contributor-editor-sheet';
import { ContributorCategoryMapDialog } from './contributor-category-map-dialog';
import { ContributorReviewsDialog } from './contributor-reviews-dialog';
import { ContributorAwardsDialog } from './contributor-awards-dialog';

const PAGE_SIZES = [25, 50, 100];
const ALL = '0';
const YES = '1';

const Bool = ({ on }: { on?: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

interface DraftFilters {
	searchText: string;
	categories: number[];
	isPendingReview: string;
	hasWonAward: string;
	searchTextErpSyncCode: string;
}

const EMPTY_FILTERS: DraftFilters = { searchText: '', categories: [], isPendingReview: ALL, hasWonAward: ALL, searchTextErpSyncCode: '' };

/** Contributors list (Συντελεστές) — server-paged grid with additional filtering, editor and per-row sub-views. */
export function ContributorsGridView() {
	const { t } = useTranslation();

	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(25);
	// Default to id-desc (PK index → sub-second on the 180k-row table incl. requireTotalCount).
	// The legacy admin defaults to a Surname sort, but that nvarchar column is unindexed and the
	// COUNT + sort over 180k rows is a multi-second→timeout hot path; Name/Surname stay available
	// as on-demand sortable headers (server selectors fn/ln) for narrowed/explicit use.
	const [sortKey, setSortKey] = useState<ContributorSortKey | null>('id');
	const [sortDir, setSortDir] = useState<SortDir>('desc');

	const [showFilters, setShowFilters] = useState(false);
	const [draft, setDraft] = useState<DraftFilters>(EMPTY_FILTERS);
	const [applied, setApplied] = useState<AdditionalFiltering>({});

	const { data: categories } = useContributorCategoriesList();
	const categoryName = useMemo(() => new Map((categories ?? []).map(c => [c.id, c.nm])), [categories]);

	const { data, isLoading, isError, refetch, isFetching } = useContributorsList({
		skip: page * pageSize,
		take: pageSize,
		sortSelector: sortKey,
		sortDir,
		filters: applied
	});
	const { remove } = useContributorMutations();

	const [editorOpen, setEditorOpen] = useState(false);
	const [editingId, setEditingId] = useState<number | null>(null);
	const [deleteRow, setDeleteRow] = useState<ContributorToken | null>(null);
	const [categoriesFor, setCategoriesFor] = useState<ContributorToken | null>(null);
	const [reviewsFor, setReviewsFor] = useState<ContributorToken | null>(null);
	const [awardsFor, setAwardsFor] = useState<ContributorToken | null>(null);

	const rows = data?.items ?? [];
	const total = data?.totalCount ?? 0;
	const pageCount = Math.max(1, Math.ceil(total / pageSize));

	const toggleSort = (key: ContributorSortKey) => {
		if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
		else {
			setSortKey(key);
			setSortDir('asc');
		}
		setPage(0);
	};

	const SortHeader = ({ col, label }: { col: ContributorSortKey; label: string }) => (
		<button type="button" onClick={() => toggleSort(col)} className="inline-flex items-center gap-1 font-medium">
			{label}
			{sortKey === col && (sortDir === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />)}
		</button>
	);

	const applyFilters = () => {
		setApplied({
			searchText: draft.searchText.trim() || null,
			searchTextErpSyncCode: draft.searchTextErpSyncCode.trim() || null,
			categories: draft.categories,
			isPendingReview: Number(draft.isPendingReview),
			hasWonAward: Number(draft.hasWonAward)
		});
		setPage(0);
	};

	const clearFilters = () => {
		setDraft(EMPTY_FILTERS);
		setApplied({});
		setPage(0);
	};

	const openEditor = (id: number | null) => {
		setEditingId(id);
		setEditorOpen(true);
	};

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	const fullName = (c: ContributorToken) => [c.fn, c.ln].filter(Boolean).join(' ').trim() || `#${c.id}`;

	return (
		<div className="flex flex-col gap-3">
			<div className="flex items-start justify-between gap-4 border-b border-border pb-4">
				<div>
					<h1 className="text-2xl font-semibold tracking-[-0.015em] text-foreground">{t('contributors.list.title')}</h1>
					<p className="mt-1 text-sm text-foreground-muted">{t('contributors.list.subtitle', { count: total })}</p>
				</div>
				<div className="flex items-center gap-2">
					<Button variant="outline" onClick={() => setShowFilters(s => !s)}>
						<SlidersHorizontal size={16} /> {t('contributors.list.filters.toggle')}
					</Button>
					<Button onClick={() => openEditor(null)}>
						<Plus size={16} /> {t('contributors.list.add')}
					</Button>
				</div>
			</div>

			{showFilters && (
				<div className="flex flex-col gap-3 rounded-md border border-border bg-surface p-4">
					<div className="grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-5">
						<div className="flex flex-col gap-1">
							<label className="text-xs text-foreground-muted">{t('contributors.list.filters.name')}</label>
							<Input value={draft.searchText} onChange={e => setDraft(d => ({ ...d, searchText: e.target.value }))} />
						</div>
						<div className="flex flex-col gap-1">
							<label className="text-xs text-foreground-muted">{t('contributors.list.filters.categories')}</label>
							<MultiSelect<number>
								value={draft.categories}
								onChange={next => setDraft(d => ({ ...d, categories: next }))}
								options={(categories ?? []).map(c => c.id!).filter(id => id != null)}
								getKey={id => String(id)}
								getLabel={id => categoryName.get(id) ?? String(id)}
								placeholder={t('contributors.list.filters.categories')}
							/>
						</div>
						<div className="flex flex-col gap-1">
							<label className="text-xs text-foreground-muted">{t('contributors.list.filters.pendingReview')}</label>
							<Select value={draft.isPendingReview} onValueChange={v => setDraft(d => ({ ...d, isPendingReview: v }))}>
								<SelectTrigger className="w-full">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={ALL}>{t('contributors.list.filters.all')}</SelectItem>
									<SelectItem value={YES}>{t('contributors.list.filters.yes')}</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="flex flex-col gap-1">
							<label className="text-xs text-foreground-muted">{t('contributors.list.filters.award')}</label>
							<Select value={draft.hasWonAward} onValueChange={v => setDraft(d => ({ ...d, hasWonAward: v }))}>
								<SelectTrigger className="w-full">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={ALL}>{t('contributors.list.filters.all')}</SelectItem>
									<SelectItem value={YES}>{t('contributors.list.filters.yes')}</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="flex flex-col gap-1">
							<label className="text-xs text-foreground-muted">{t('contributors.list.filters.erp')}</label>
							<Input value={draft.searchTextErpSyncCode} onChange={e => setDraft(d => ({ ...d, searchTextErpSyncCode: e.target.value }))} />
						</div>
					</div>
					<div className="flex justify-end gap-2">
						<Button variant="ghost" onClick={clearFilters}>
							{t('contributors.list.filters.clear')}
						</Button>
						<Button onClick={applyFilters}>{t('contributors.list.filters.apply')}</Button>
					</div>
				</div>
			)}

			<div className="overflow-x-auto rounded-md border border-border bg-surface">
				{isError ? (
					<div className="flex flex-col items-center gap-3 py-16 text-center">
						<AlertCircle size={40} className="text-danger-fg" strokeWidth={1.25} />
						<p className="text-sm text-foreground-muted">{t('common.noResults')}</p>
						<Button variant="outline" onClick={() => void refetch()}>
							{t('common.retry')}
						</Button>
					</div>
				) : isLoading ? (
					<div className="flex flex-col gap-2 p-3">
						{Array.from({ length: 10 }).map((_, i) => (
							<Skeleton key={i} className="h-10 w-full rounded-md" />
						))}
					</div>
				) : rows.length === 0 ? (
					<div className="flex flex-col items-center gap-3 py-16 text-center">
						<UserSquare2 size={36} className="text-foreground-disabled" strokeWidth={1.25} />
						<p className="text-sm text-foreground-muted">{t('common.noResults')}</p>
					</div>
				) : (
					<table className={cn('w-full border-collapse text-sm', isFetching && 'opacity-60')}>
						<thead className="bg-elevated text-xs text-foreground-muted">
							<tr className="border-b border-border">
								<th className="px-3 py-2 text-left">
									<SortHeader col="fn" label={t('contributors.list.columns.name')} />
								</th>
								<th className="px-3 py-2 text-left">
									<SortHeader col="ln" label={t('contributors.list.columns.surname')} />
								</th>
								<th className="px-3 py-2 text-left font-medium">{t('contributors.list.columns.categories')}</th>
								<th className="px-3 py-2 text-center font-medium">{t('contributors.list.columns.reviews')}</th>
								<th className="px-3 py-2 text-center font-medium">{t('contributors.list.columns.awards')}</th>
								<th className="px-3 py-2 text-center font-medium">{t('contributors.list.columns.visible')}</th>
								<th className="px-3 py-2 text-left font-medium">{t('contributors.list.columns.erp')}</th>
								<th className="px-2 py-2" style={{ width: 48 }} />
							</tr>
						</thead>
						<tbody>
							{rows.map(c => (
								<tr key={c.id} className="group border-b border-border/60 hover:bg-muted/50">
									<td className="px-3 py-2">
										<button
											type="button"
											onClick={() => openEditor(c.id ?? null)}
											className="truncate text-left font-medium text-foreground hover:underline">
											{c.fn || '—'}
										</button>
									</td>
									<td className="px-3 py-2 text-foreground-muted">{c.ln || '—'}</td>
									<td className="px-3 py-2">
										<div className="flex flex-col gap-0.5">
											{(c.contributorCategoryMap ?? []).map((m, i) => (
												<span key={i} className="inline-flex items-center gap-1.5 text-xs text-foreground-muted">
													{m.chex && (
														<span
															className="inline-block h-2.5 w-2.5 rounded-full border border-border"
															style={{ backgroundColor: m.chex }}
														/>
													)}
													{m.contributorCategoryName}
												</span>
											))}
										</div>
									</td>
									<td className="px-3 py-2">
										<div className="flex items-center justify-center gap-1">
											<span className="rounded-full bg-success-subtle px-1.5 py-0.5 text-xs text-success-fg">
												{c.num_of_accepted_reviews ?? 0}
											</span>
											<span className="rounded-full bg-warning-subtle px-1.5 py-0.5 text-xs text-warning-fg">
												{c.num_of_pending_reviews ?? 0}
											</span>
											<span className="rounded-full bg-danger-subtle px-1.5 py-0.5 text-xs text-danger-fg">
												{c.num_of_declined_reviews ?? 0}
											</span>
										</div>
									</td>
									<td className="px-3 py-2 text-center">
										{(c.num_of_awards ?? 0) > 0 ? (
											<span className="inline-flex items-center justify-center gap-1 text-foreground">
												<Award size={14} className="text-warning-fg" /> {c.num_of_awards}
											</span>
										) : (
											<span className="text-foreground-disabled">0</span>
										)}
									</td>
									<td className="px-3 py-2 text-center">
										<Bool on={c.iv} />
									</td>
									<td className="px-3 py-2 font-mono text-xs text-foreground-muted">{c.erpcd || '—'}</td>
									<td className="px-2 py-1 text-center">
										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<Button variant="ghost" size="icon" className="h-7 w-7" aria-label={t('common.sections')}>
													<MoreVertical size={15} />
												</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent align="end" className="w-52">
												<DropdownMenuItem onSelect={() => openEditor(c.id ?? null)}>
													<Pencil size={14} /> {t('common.edit')}
												</DropdownMenuItem>
												<DropdownMenuSeparator />
												<DropdownMenuItem onSelect={() => setCategoriesFor(c)}>
													<Link2 size={14} /> {t('contributors.list.actions.categories')}
												</DropdownMenuItem>
												<DropdownMenuItem onSelect={() => setReviewsFor(c)}>
													<MessageSquare size={14} /> {t('contributors.list.actions.reviews')}
												</DropdownMenuItem>
												<DropdownMenuItem onSelect={() => setAwardsFor(c)}>
													<Award size={14} /> {t('contributors.list.actions.awards')}
												</DropdownMenuItem>
												<DropdownMenuSeparator />
												<DropdownMenuItem variant="destructive" onSelect={() => setDeleteRow(c)}>
													<Trash2 size={14} /> {t('common.delete')}
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>

			{/* Pagination */}
			<div className="flex flex-wrap items-center justify-between gap-3 text-sm text-foreground-muted">
				<div className="flex items-center gap-2">
					<span>{t('system.grid.rowsPerPage')}</span>
					<select
						value={pageSize}
						onChange={e => {
							setPageSize(Number(e.target.value));
							setPage(0);
						}}
						className="h-8 rounded-md border border-border bg-surface px-2">
						{PAGE_SIZES.map(s => (
							<option key={s} value={s}>
								{s}
							</option>
						))}
					</select>
				</div>
				<div className="flex items-center gap-2">
					<span>{t('contributors.list.pageInfo', { page: page + 1, pages: pageCount, total })}</span>
					<Button variant="outline" size="sm" disabled={page <= 0} onClick={() => setPage(p => Math.max(0, p - 1))}>
						{t('common.previous')}
					</Button>
					<Button variant="outline" size="sm" disabled={page >= pageCount - 1} onClick={() => setPage(p => p + 1)}>
						{t('common.next')}
					</Button>
				</div>
			</div>

			<ContributorEditorSheet open={editorOpen} onOpenChange={setEditorOpen} contributorId={editingId} />

			<ContributorCategoryMapDialog
				open={Boolean(categoriesFor)}
				onOpenChange={open => !open && setCategoriesFor(null)}
				contributorId={categoriesFor?.id ?? null}
				contributorName={categoriesFor ? fullName(categoriesFor) : undefined}
			/>
			<ContributorReviewsDialog
				open={Boolean(reviewsFor)}
				onOpenChange={open => !open && setReviewsFor(null)}
				contributorId={reviewsFor?.id ?? null}
				contributorName={reviewsFor ? fullName(reviewsFor) : undefined}
			/>
			<ContributorAwardsDialog
				open={Boolean(awardsFor)}
				onOpenChange={open => !open && setAwardsFor(null)}
				contributorId={awardsFor?.id ?? null}
				contributorName={awardsFor ? fullName(awardsFor) : undefined}
			/>

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('contributors.list.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('contributors.list.deleteBody', { name: deleteRow ? fullName(deleteRow) : '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
